import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { money } from "../lib/format";
import { toast } from "sonner";
import { Save, History, Plus, X } from "lucide-react";

const SOURCES = ["QuickBooks Payroll", "Wagepoint", "ADP", "Ceridian", "Payworks", "Manual", "Other"];

// Phase 5.2b-2b — Prior-System (opening) per-code deduction YTD.
// Flexible per-code rows; backend enforces the annual payroll deduction limit.
export default function OpeningDeductionYtd({ companyId, employee, canEdit, year }) {
  const eid = employee?.employee_id;
  const [codes, setCodes] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ deduction_code: "", amount: "", source_system: "Manual", source_reference: "", notes: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(null);

  const load = useCallback(() => {
    if (!companyId || !eid) return;
    api.get(`/companies/${companyId}/deduction-codes`).then((r) => setCodes(r.data.codes || [])).catch(() => setCodes([]));
    api.get(`/companies/${companyId}/opening-deduction-ytd?tax_year=${year}&employee_id=${eid}`)
      .then((r) => setRows(r.data.opening_deduction_ytd || [])).catch(() => setRows([]));
  }, [companyId, eid, year]);
  useEffect(() => { load(); }, [load]);

  const existingFor = (code) => rows.find((r) => (r.code_norm || "").toLowerCase() === (code || "").toLowerCase());
  const editRow = (r) => setForm({ deduction_code: r.deduction_code, amount: r.amount, source_system: r.source_system || "Manual", source_reference: r.source_reference || "", notes: r.notes || "", reason: "" });

  const save = async () => {
    if (!form.deduction_code || form.amount === "") { toast.error("Select a code and enter an amount"); return; }
    const existing = existingFor(form.deduction_code);
    if (existing && Number(existing.amount) !== Number(form.amount) && !form.reason.trim()) {
      toast.error("A reason is required to change an existing amount"); return;
    }
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/opening-deduction-ytd`, {
        employee_id: eid, tax_year: year, deduction_code: form.deduction_code, amount: Number(form.amount),
        source_system: form.source_system, source_reference: form.source_reference, notes: form.notes, reason: form.reason,
      });
      toast.success("Prior-system deduction YTD saved");
      setForm({ deduction_code: "", amount: "", source_system: "Manual", source_reference: "", notes: "", reason: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const editingExisting = existingFor(form.deduction_code);
  const amountChanged = editingExisting && Number(editingExisting.amount) !== Number(form.amount);

  return (
    <div data-testid="opening-deduction-ytd" className="border-t border-border pt-5 mt-6">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold tracking-tight text-sm">Prior-System Deduction YTD <span className="text-muted-foreground font-normal">· {year}</span></h4>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Per-code deductions carried over from the prior payroll system. Used to compute the remaining annual payroll deduction limit on current payroll — <span className="font-medium">not</span> a CRA contribution room.</p>

      {rows.length > 0 && (
        <div className="border border-border mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2">Last modified</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.odytd_id} data-testid={`odytd-row-${r.deduction_code}`} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 font-medium">{r.deduction_code}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.amount)}</td>
                  <td className="px-3 py-2 text-xs">{r.source_system}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{r.source_reference || "—"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{(r.updated_at || "").slice(0, 10)} · {r.updated_by || ""}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button data-testid={`odytd-history-${r.deduction_code}`} onClick={() => setShowHistory(showHistory === r.odytd_id ? null : r.odytd_id)} className="text-muted-foreground hover:text-black inline-flex items-center gap-1 text-xs mr-2"><History className="h-3 w-3" /> Audit ({(r.audit || []).length})</button>
                    {canEdit && <button data-testid={`odytd-edit-${r.deduction_code}`} onClick={() => editRow(r)} className="text-xs border border-border px-2 py-0.5 hover:border-black transition-colors">Edit</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {showHistory && (
            <div data-testid="odytd-audit-detail" className="border-t border-border bg-neutral-50 px-3 py-2 text-xs">
              {(rows.find((r) => r.odytd_id === showHistory)?.audit || []).map((a, i) => (
                <div key={i} className="py-1 border-b border-border last:border-0 flex flex-wrap gap-x-4">
                  <span className="text-muted-foreground">{(a.changed_at || "").slice(0, 16)}</span>
                  <span>{a.previous_amount == null ? "—" : money(a.previous_amount)} → <span className="font-medium">{money(a.new_amount)}</span></span>
                  <span className="text-muted-foreground">by {a.changed_by}</span>
                  {a.reason && <span className="italic">"{a.reason}"</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {canEdit ? (
        <div className="border border-border p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-1">Deduction code</label>
              <select data-testid="odytd-code" value={form.deduction_code} onChange={(e) => setForm({ ...form, deduction_code: e.target.value })} className="w-full border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
                <option value="">Select a deduction code…</option>
                {codes.filter((c) => c.active !== false).map((c) => (
                  <option key={c.code_id} value={c.code}>{c.code} — {c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-1">Amount (YTD)</label>
              <input data-testid="odytd-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full border border-input bg-white px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-1">Source system</label>
              <select data-testid="odytd-source" value={form.source_system} onChange={(e) => setForm({ ...form, source_system: e.target.value })} className="w-full border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-1">Source reference</label>
              <input data-testid="odytd-reference" value={form.source_reference} onChange={(e) => setForm({ ...form, source_reference: e.target.value })} placeholder="e.g. QB export Jun-2026" className="w-full border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-1">Notes</label>
            <input data-testid="odytd-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
          </div>
          {amountChanged && (
            <>
              <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="odytd-edit-warning">
                Changing prior-system deduction YTD may change annual deduction limits on current payroll calculations. Existing review/approval on unposted payroll will be re-validated.
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-amber-700 block mb-1">Reason for change (required)</label>
                <input data-testid="odytd-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why is this amount being corrected?" className="w-full border border-amber-300 bg-amber-50 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              </div>
            </>
          )}
          <button data-testid="odytd-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : editingExisting ? "Save correction" : "Add prior-system YTD"}
          </button>
          {form.deduction_code && (
            <button data-testid="odytd-clear-form" onClick={() => setForm({ deduction_code: "", amount: "", source_system: "Manual", source_reference: "", notes: "", reason: "" })} className="ml-2 inline-flex items-center gap-1 border border-border px-3 py-2 text-sm hover:border-black transition-colors"><X className="h-3.5 w-3.5" /> Clear</button>
          )}
        </div>
      ) : (
        rows.length === 0 && <p className="text-xs text-muted-foreground">No prior-system deduction YTD recorded.</p>
      )}
    </div>
  );
}
